import React, {
  useState,
  useContext,
  useMemo,
  Suspense,
  useEffect,
} from 'react';
import styled from 'styled-components';
import { SwitcherWrapper } from '@v2/components/GDS/Switcher';
import type { SwitchItem } from 'pepsico-ds/dist/lib/Atoms/Switcher/Switcher';
import { FilterContext } from '@components/FilterBar/FilterProvider';
import CmsRenderer from '../CmsRenderer';
import { getCustomComponent } from '../customComponentRegistry';
import {
  evaluateAvailability,
  AvailabilityRules,
} from '../utils/availabilityUtils';
import Loader from '@components/Loader/Loader';
import { useFilterV2 } from '@hooks/useFilterV2';
import { INTEGRATED_PRODUCT_FILTER_ID } from '@components/IntegratedProductFilter/IntegratedProductFilter';
import { useTabContext } from 'components/FilterBar/hook/useTabContext';

// AvailabilityRules type imported from ../utils/availabilityUtils

/**
 * Grid content linked from SwitcherItem.contentSlug
 */
interface GridContent {
  id: string;
  slug: string;
  _modelApiKey: string;
  blocks?: any[];
  [key: string]: any;
}

/**
 * Header content block from Switcher.headerContent (Modular Content field)
 * Returns an array of CustomReactComponent blocks
 */
interface HeaderContentBlock {
  id: string;
  slug?: string;
  reactComponentSlug: string;
  _modelApiKey: string;
}

interface SwitcherItemData {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
  availabilityRules?: AvailabilityRules;
  contentSlug?: GridContent; // Now a linked Grid record, not a string
  _modelApiKey: string;
}

interface CmsSwitcherProps {
  id?: string;
  slug?: string;
  size?: 'medium' | 'large';
  defaultIndex?: number;
  headerContent?: HeaderContentBlock | HeaderContentBlock[]; // Single block or array of blocks
  items?: SwitcherItemData[];
  children?: React.ReactNode;
}

/**
 * Styled components for Switcher layout
 */
const SwitcherContainer = styled.div`
  width: 100%;
`;

const SwitcherHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
`;

const SwitcherTabsContainer = styled.div`
  flex: 1;
`;

const SwitcherFilterContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const SwitcherContent = styled.div`
  margin-top: 16px;
`;

/**
 * Mapping of switcher item values to integratedProduct filter values (appCode)
 * Used to sync CmsSwitcher selection with the integratedProduct filter in filterSliceV2
 */
const SWITCHER_VALUE_TO_APP_CODE: Record<string, number> = {
  pepsiconnect: 1,
  pepsell: 2,
};

// evaluateAvailability imported from ../utils/availabilityUtils

export const CmsSwitcher: React.FC<CmsSwitcherProps> = ({
  size,
  defaultIndex,
  headerContent,
  items,
  children,
}) => {
  const { selectedMarket, currentTabName } = useContext(FilterContext);

  // Redux filter management - sync switcher selection with integratedProduct filter
  const { updateFilterValue, updateFilterEnabled, updateFilterLoading } =
    useFilterV2();

  // Process items with availability rules
  const processedItems = useMemo(() => {
    if (!items) {
      return [];
    }

    return items.map((item) => ({
      ...item,
      disabled:
        item.disabled ||
        evaluateAvailability(item.availabilityRules, {
          marketId: selectedMarket?.marketId,
        }),
    }));
  }, [items, selectedMarket?.marketId]);

  const getFirstEnabledIndex = (itemsList: typeof processedItems) => {
    if (defaultIndex !== undefined && itemsList.length > 0) {
      const item = itemsList[defaultIndex];
      if (item && !item.disabled) {
        return defaultIndex;
      }
    }
    const firstEnabled = itemsList.findIndex((item) => !item.disabled);
    return firstEnabled !== -1 ? firstEnabled : 0;
  };

  const [selectedIndex, setSelectedIndex] = useState(() =>
    getFirstEnabledIndex(processedItems)
  );

  // Derive current sub-tab name from the selected switcher item's label
  const currentSubTabName = processedItems[selectedIndex]?.label ?? null;

  // Sync tab/sub-tab context with FilterContext for FrequencyFilter
  useTabContext(currentTabName, currentSubTabName);

  // Pre-register integratedProduct filter on mount and set initial value based on default selection
  useEffect(() => {
    updateFilterEnabled(INTEGRATED_PRODUCT_FILTER_ID, true);
    updateFilterLoading(INTEGRATED_PRODUCT_FILTER_ID, false);

    // Set initial value based on default selected item
    const initialItem = processedItems[selectedIndex];
    const appCode =
      SWITCHER_VALUE_TO_APP_CODE[initialItem?.value?.toLowerCase()] ?? 1;
    updateFilterValue(INTEGRATED_PRODUCT_FILTER_ID, appCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-navigate to first enabled item when market changes and current item becomes disabled
  // Also sync the integratedProduct filter value when auto-navigating
  useEffect(() => {
    const currentItem = processedItems[selectedIndex];

    if (currentItem?.disabled) {
      const firstEnabledIndex = getFirstEnabledIndex(processedItems);
      if (firstEnabledIndex !== selectedIndex) {
        setSelectedIndex(firstEnabledIndex);

        // Sync integratedProduct filter with the new selection
        const newItem = processedItems[firstEnabledIndex];
        const appCode =
          SWITCHER_VALUE_TO_APP_CODE[newItem?.value?.toLowerCase()] ?? 1;
        updateFilterValue(INTEGRATED_PRODUCT_FILTER_ID, appCode);
      }
    }
  }, [processedItems, selectedMarket?.marketId]);

  const switchItems: SwitchItem[] = processedItems.map((item) => ({
    label: item.label,
    value: item.value,
    disabled: item.disabled ?? false,
  }));

  const handleSelectionChange = (newIndex: number) => {
    const item = processedItems[newIndex];
    if (!item?.disabled) {
      setSelectedIndex(newIndex);

      // Sync with integratedProduct filter in filterSliceV2
      const appCode =
        SWITCHER_VALUE_TO_APP_CODE[item?.value?.toLowerCase()] ?? 1;
      updateFilterValue(INTEGRATED_PRODUCT_FILTER_ID, appCode);
    }
  };

  /**
   * Render content for the selected switcher item
   * Now supports linked Grid content via contentSlug
   */
  const renderContent = () => {
    const selectedItem = processedItems[selectedIndex];

    // If contentSlug is a linked Grid record, render it via CmsRenderer
    if (
      selectedItem?.contentSlug &&
      typeof selectedItem.contentSlug === 'object'
    ) {
      return <CmsRenderer node={selectedItem.contentSlug} />;
    }

    // Legacy: If contentSlug is a string, filter children by slug
    if (
      selectedItem?.contentSlug &&
      typeof selectedItem.contentSlug === 'string'
    ) {
      return React.Children.toArray(children).filter((child: any) => {
        return child?.props?.slug === selectedItem.contentSlug;
      });
    }

    // No contentSlug mapping, show all children
    return children;
  };

  return (
    <SwitcherContainer>
      <SwitcherHeader>
        <SwitcherTabsContainer>
          <SwitcherWrapper
            items={switchItems}
            selectedIndex={selectedIndex}
            onChangeSelection={handleSelectionChange}
            size={size}
          />
        </SwitcherTabsContainer>

        {headerContent && (
          <SwitcherFilterContainer>
            <Suspense fallback={<Loader />}>
              {(() => {
                // Normalize to array (DatoCMS may return single object or array)
                const blocks = Array.isArray(headerContent)
                  ? headerContent
                  : [headerContent];
                return blocks.map((block) => {
                  const HeaderComponent = getCustomComponent(
                    block.reactComponentSlug
                  );
                  if (!HeaderComponent) {
                    console.warn(
                      `[CmsSwitcher] No custom component registered for slug: "${block.reactComponentSlug}"`
                    );
                    return null;
                  }
                  return (
                    <HeaderComponent
                      key={block.id}
                      slug={block.slug}
                      isCmsRender={true}
                    />
                  );
                });
              })()}
            </Suspense>
          </SwitcherFilterContainer>
        )}
      </SwitcherHeader>

      <SwitcherContent>{renderContent()}</SwitcherContent>
    </SwitcherContainer>
  );
};

export default CmsSwitcher;
